import { assertExactlyOne } from '@adornis/assert/assert-exactly-one.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type ClientContext, context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { LMSCourse } from '../entities/LMSCourse.js';
import { LMSAttemptAnswer, LMSCourseAttempt } from '../entities/LMSCourseAttempt.js';
import { LMSCourseEnrollment, LMSEnrollmentStatus } from '../entities/LMSCourseEnrollment.js';
import { LMSCoursePage, LMSCoursePageType } from '../entities/LMSCoursePage.js';
import { createLMSPayload, getABAC, LMSAction } from '../init.js';
import { expectedError } from './expected-error.js';
import type { Maybe } from '@adornis/base/utilTypes.js';


export function getLatestAnswersByCoursePageId(answers: Maybe<LMSAttemptAnswer[]>): LMSAttemptAnswer[] {
  if (!answers?.length) return [];

  const latestByPageId = new Map<string, LMSAttemptAnswer>();
  for (const answer of answers) {
    if (!answer?.coursePageId) continue;
    latestByPageId.set(String(answer.coursePageId), answer);
  }

  return [...latestByPageId.values()];
}

const getFailedCompletedAttempts = (attempts: LMSCourseAttempt[] | null | undefined) =>
  (attempts ?? []).filter(attempt => !!attempt.completedAt && !attempt.passed).length;

const normalizeMaxAttempts = (maxAttempts: number | null | undefined) =>
  typeof maxAttempts === 'number' && maxAttempts > 0 ? maxAttempts : null;

export const getCoursesForUser = registerQuery({
  operationName: 'getCoursesForUser',
  type: () => [LMSCourseEnrollment],
  params: [{ name: 'userId', type: () => String }],
  resolve:
    (userId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment[]> => {
      const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const result = await col
        .aggregate<LMSCourseEnrollment>([
          { $match: { userId } },
          { $lookup: { from: LMSCourse._collectionName, localField: 'courseId', foreignField: '_id', as: '_course' } },
          { $match: { '_course.archived': { $ne: true } } },
          { $project: { _course: 0 } },
          { $sort: { createdAt: -1 } },
        ])
        .toArray();
      await Promise.all(
        result.map(enrollment =>
          getABAC().assert(createLMSPayload({ action: LMSAction.ViewCourse, courseId: enrollment.courseId! })),
        ),
      );
      return result;
    },
});

export const getCoursePages = registerQuery({
  operationName: 'getCoursePages',
  type: () => [LMSCoursePage],
  params: [{ name: 'courseId', type: () => String }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve:
    (courseId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCoursePage>): Promise<LMSCoursePage[]> => {
      await getABAC().assert(createLMSPayload({ action: LMSAction.ViewCoursePages, courseId }));
      const col = await getRawCollection<LMSCoursePage>(LMSCoursePage._collectionName);
      return (await col.find<LMSCoursePage>({ courseId }).sort({ order: 1 }).toArray()).map(p =>
        constructValue<LMSCoursePage>(p),
      );
    },
});

export const getCourseById = registerQuery({
  operationName: 'getLMSCourseById',
  type: () => LMSCourse,
  params: [{ name: 'courseId', type: () => String }],
  resolve:
    (courseId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourse>): Promise<LMSCourse | null> => {
      await getABAC().assert(
        createLMSPayload({ action: LMSAction.ViewCourse, userId: (context as ClientContext).userID, courseId }),
      );
      const col = await getRawCollection<LMSCourse>(LMSCourse._collectionName);
      return col.findOne<LMSCourse>({ _id: courseId });
    },
});

export const getEnrollmentByUserAndCourse = registerQuery({
  operationName: 'getLMSEnrollmentByUserAndCourse',
  type: () => LMSCourseEnrollment,
  params: [
    { name: 'userId', type: () => String },
    { name: 'courseId', type: () => String },
  ],
  resolve:
    (userId: string, courseId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment | null> => {
      const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const results = await col.find<LMSCourseEnrollment>({ userId, courseId }).toArray();
      if (results.length === 0) return null;
      const enrollment = constructValue<LMSCourseEnrollment>(assertExactlyOne(results)[0]);
      await getABAC().assert(
        createLMSPayload({ action: LMSAction.CourseEnrollmentRead, enrollmentUserId: enrollment.userId! }),
      );
      return enrollment;
    },
});

export const submitPageAnswer = registerMutation({
  operationName: 'submitLMSPageAnswer',
  type: () => LMSCourseEnrollment,
  params: [
    { name: 'enrollmentId', type: () => String },
    { name: 'coursePageId', type: () => String },
    { name: 'selectedOptionIds', type: () => [String] },
  ],
  resolve:
    (enrollmentId: string, coursePageId: string, selectedOptionIds: string[]) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment> => {
      // Pre-fetch enrollment to get owner userId for policy check
      const enrollCol = await getRawCollection<EntityData<LMSCourseEnrollment>>(LMSCourseEnrollment._collectionName);
      const enrollment = await enrollCol.findOne<EntityData<LMSCourseEnrollment>>({ _id: enrollmentId });
      if (!enrollment) throw expectedError('Enrollment not found');
      await getABAC().assert(
        createLMSPayload({ action: LMSAction.SubmitAnswer, enrollmentUserId: enrollment.userId ?? undefined }),
      );

      const pageCol = await getRawCollection<LMSCoursePage>(LMSCoursePage._collectionName);
      const page = await pageCol.findOne<LMSCoursePage>({ _id: coursePageId });
      if (!page) throw expectedError('Course page not found');

      const correctIds = (page.answerOptions ?? []).filter(o => o.isCorrect).map(o => String((o as any)._id));
      const selectedIds = selectedOptionIds.map(id => String(id));
      const isCorrect = correctIds.length === selectedIds.length && correctIds.every(id => selectedIds.includes(id));

      const answer = new LMSAttemptAnswer({ coursePageId, selectedOptionIds, isCorrect }).toJSON();

      // Re-fetch enrollment
      const freshEnrollment = await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId });
      if (!freshEnrollment) throw expectedError('Enrollment not found');

      const attempts = freshEnrollment.attempts ?? [];
      const lastAttempt = attempts[attempts.length - 1];

      // Require an existing incomplete attempt. Reachable whenever the player's enrollment data is
      // stale (a second tab, a bookmarked player URL, an admin reset mid-attempt), so it is a client
      // state mismatch — not a server fault.
      if (!lastAttempt || lastAttempt.completedAt) {
        throw expectedError('No in-progress attempt found. Please start a new attempt first.');
      }

      const lastIdx = attempts.length - 1;
      await enrollCol.updateOne(
        { _id: enrollmentId },
        { $pull: { [`attempts.${lastIdx}.answers`]: { coursePageId } } },
      );
      await enrollCol.updateOne({ _id: enrollmentId }, { $push: { [`attempts.${lastIdx}.answers`]: answer } });

      return constructValue<LMSCourseEnrollment>(await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId }));
    },
});

export const getEnrollmentsForCourse = registerQuery({
  operationName: 'getEnrollmentsForCourse',
  type: () => [LMSCourseEnrollment],
  params: [{ name: 'courseId', type: () => String }],
  resolve:
    (courseId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment[]> => {
      await getABAC().assert(createLMSPayload({ action: LMSAction.CourseEnrollmentRead }));
      const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      return col.find<LMSCourseEnrollment>({ courseId }).sort({ createdAt: -1 }).toArray();
    },
});

export const enrollUserInCourse = registerMutation({
  operationName: 'enrollUserInCourse',
  type: () => LMSCourseEnrollment,
  params: [
    { name: 'courseId', type: () => String },
    { name: 'userId', type: () => String },
  ],
  resolve:
    (courseId: string, userId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment> => {
      await getABAC().assert(createLMSPayload({ action: LMSAction.CourseEnrollmentCreate }));
      const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const existing = await col.findOne<LMSCourseEnrollment>({ courseId, userId });
      if (existing) return existing;
      const enrollment = new LMSCourseEnrollment({ courseId, userId });
      await enrollment.create();
      return enrollment;
    },
});

export const removeEnrollment = registerMutation({
  operationName: 'removeEnrollment',
  type: () => Boolean,
  params: [{ name: 'enrollmentId', type: () => String }],
  resolve: (enrollmentId: string) => async (): Promise<boolean> => {
    await getABAC().assert(createLMSPayload({ action: LMSAction.CourseEnrollmentDelete }));
    const col = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
    const enrollment = await col.findOne<LMSCourseEnrollment>({ _id: enrollmentId });
    if (!enrollment) return false;

    const hasCompletedAttempt = (enrollment.attempts ?? []).some(a => !!a.completedAt);
    if (hasCompletedAttempt) {
      throw new Error('Zuweisung kann nicht gelöscht werden, da bereits Versuche vorhanden sind.');
    }

    const result = await col.deleteOne({ _id: enrollmentId });
    return result.deletedCount > 0;
  },
});

// Soft delete: stamp `deletedAt` so the course disappears from every list while its data (pages,
// enrollments, attempts) is preserved. `subscribeScopedCoursesPaginated` treats `deletedAt == null`
// (null or missing) as live.
export const softDeleteCourse = registerMutation({
  operationName: 'softDeleteLMSCourse',
  type: () => Boolean,
  params: [{ name: 'courseId', type: () => String }],
  resolve: (courseId: string) => async (): Promise<boolean> => {
    await getABAC().assert(createLMSPayload({ action: LMSAction.CourseDelete, courseId }));
    const col = await getRawCollection<EntityData<LMSCourse>>(LMSCourse._collectionName);
    const result = await col.updateOne({ _id: courseId }, { $set: { deletedAt: new Date() } });
    return result.matchedCount === 1;
  },
});

export const deleteCoursePage = registerMutation({
  operationName: 'deleteCoursePage',
  type: () => Boolean,
  params: [{ name: 'pageId', type: () => String }],
  resolve: (pageId: string) => async (): Promise<boolean> => {
    await getABAC().assert(createLMSPayload({ action: LMSAction.CourseUpdate }));
    const col = await getRawCollection<LMSCoursePage>(LMSCoursePage._collectionName);
    const result = await col.deleteOne({ _id: pageId });
    return result.deletedCount > 0;
  },
});

export const reorderCoursePages = registerMutation({
  operationName: 'reorderCoursePages',
  type: () => Boolean,
  params: [
    { name: 'courseId', type: () => String },
    { name: 'pageIds', type: () => [String] },
  ],
  resolve: (courseId: string, pageIds: string[]) => async (): Promise<boolean> => {
    await getABAC().assert(createLMSPayload({ action: LMSAction.CourseUpdate, courseId }));
    const col = await getRawCollection<LMSCoursePage>(LMSCoursePage._collectionName);

    const bulkOps = pageIds.map((id, index) => ({
      updateOne: {
        filter: { _id: id, courseId },
        update: { $set: { order: index + 1 } },
      },
    }));

    if (bulkOps.length > 0) {
      await col.bulkWrite(bulkOps);
    }

    return true;
  },
});

export const createNewAttempt = registerMutation({
  operationName: 'createNewLMSTAttempt',
  type: () => LMSCourseEnrollment,
  params: [{ name: 'enrollmentId', type: () => String }],
  resolve:
    (enrollmentId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment | null> => {
      const enrollCol = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      await getABAC().assert(createLMSPayload({ action: LMSAction.CreateAttempt, enrollmentId }));
      const enrollment = await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId });
      if (!enrollment) throw expectedError('Enrollment not found');
      if (enrollment.lockedAt) {
        throw expectedError('This enrollment is locked because the maximum failed attempts has been reached.');
      }
      if (!enrollment.courseId) throw new Error('Enrollment has no courseId');

      const courseCol = await getRawCollection<LMSCourse>(LMSCourse._collectionName);
      const course = await courseCol.findOne<LMSCourse>({ _id: enrollment.courseId });
      if (!course) throw expectedError('Course not found');

      const maxAttempts = normalizeMaxAttempts(course.maxAttempts);
      const failedCompletedAttempts = getFailedCompletedAttempts(enrollment.attempts);
      if (maxAttempts !== null && failedCompletedAttempts >= maxAttempts) {
        const lockedAt = new Date();
        await enrollCol.updateOne(
          { _id: enrollmentId },
          {
            $set: {
              lockedAt,
              lockReason: `Maximum failed attempts reached (${maxAttempts}).`,
            },
          },
        );
        throw expectedError(`You cannot create a new attempt. Maximum failed attempts reached (${maxAttempts}).`);
      }
      // Idempotent: every caller wants "there is an open attempt, take me into the player", and there
      // already is one. Throwing here only punished double taps and stale enrollment data in the
      // client — the caller's intent is already satisfied, so hand back the enrollment unchanged.
      if ((enrollment.attempts ?? []).some(attempt => !attempt.completedAt)) {
        return constructValue<LMSCourseEnrollment>(enrollment);
      }

      // Ensure attempts is an array (may be null for old enrollments)
      await enrollCol.updateOne(
        { _id: enrollmentId, attempts: { $not: { $type: 'array' } } },
        { $set: { attempts: [] } },
      );

      // Capture the client IP the attempt is started from. `relatedIPs` is built by baseql from the
      // connection's x-forwarded-for (leftmost = original client) plus x-real-ip; undefined outside a
      // client request, in which case the attempt simply carries no IP.
      const ip = (context as ClientContext).relatedIPs?.[0];

      await enrollCol.updateOne(
        { _id: enrollmentId },
        {
          $push: {
            // @ts-expect-error baseql typing is broken here
            attempts: new LMSCourseAttempt({ answers: [], ip, startedAt: new Date() }).toJSON(),
          },
          $set: { status: LMSEnrollmentStatus.IN_PROGRESS },
        },
      );

      return constructValue<LMSCourseEnrollment>(await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId }));
    },
});

export const resetEnrollment = registerMutation({
  operationName: 'resetLMSEnrollment',
  type: () => LMSCourseEnrollment,
  params: [{ name: 'enrollmentId', type: () => String }],
  resolve:
    (enrollmentId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment | null> => {
      await getABAC().assert(createLMSPayload({ action: LMSAction.CourseEnrollmentUpdate }));
      const enrollCol = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);

      await enrollCol.updateOne(
        { _id: enrollmentId },
        {
          $set: { attempts: [] },
          $unset: { scorePercent: 1, completedAt: 1, lockedAt: 1, lockReason: 1 },
        },
      );

      return constructValue<LMSCourseEnrollment>(await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId }));
    },
});

export const completeCourseAttempt = registerMutation({
  operationName: 'completeLMSCourseAttempt',
  type: () => LMSCourseEnrollment,
  params: [{ name: 'enrollmentId', type: () => String }],
  resolve:
    (enrollmentId: string) =>
    async (_gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>): Promise<LMSCourseEnrollment> => {
      const enrollCol = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const enrollment = await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId });
      if (!enrollment) throw expectedError('Enrollment not found');
      await getABAC().assert(
        createLMSPayload({ action: LMSAction.CompleteAttempt, enrollmentUserId: enrollment.userId! }),
      );
      if (!enrollment.courseId) throw new Error('Enrollment has no courseId');

      const courseCol = await getRawCollection<LMSCourse>(LMSCourse._collectionName);
      const course = await courseCol.findOne<LMSCourse>({ _id: enrollment.courseId });
      if (!course) throw expectedError('Course not found');

      const pageCol = await getRawCollection<LMSCoursePage>(LMSCoursePage._collectionName);
      const questionPages = await pageCol
        .find<LMSCoursePage>({ courseId: course._id, type: LMSCoursePageType.QUESTION })
        .toArray();

      const attempts = enrollment.attempts ?? [];
      const lastIdx = attempts.length - 1;
      const lastAttempt = attempts[lastIdx];
      if (!lastAttempt || lastAttempt.completedAt) throw expectedError('No in-progress attempt to complete');
      const answers = getLatestAnswersByCoursePageId(lastAttempt.answers ?? []);

      const totalQuestions = questionPages.length;
      const correctAnswers = answers.filter(a => a.isCorrect).length;
      const scorePercent = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
      const passed = scorePercent >= (course.passingScorePercent ?? 90);
      const maxAttempts = normalizeMaxAttempts(course.maxAttempts);
      const failedCompletedAttempts =
        getFailedCompletedAttempts(attempts) + (passed ? 0 : 1);
      const shouldLock = maxAttempts !== null && failedCompletedAttempts >= maxAttempts;

      const completedAt = new Date();

      await enrollCol.updateOne(
        { _id: enrollmentId },
        {
          $set: {
            scorePercent,
            completedAt,
            [`attempts.${lastIdx}.scorePercent`]: scorePercent,
            [`attempts.${lastIdx}.passed`]: passed,
            [`attempts.${lastIdx}.completedAt`]: completedAt,
            ...(shouldLock
              ? {
                  lockedAt: completedAt,
                  lockReason: `Maximum failed attempts reached (${maxAttempts}).`,
                }
              : {}),
          },
          ...(shouldLock ? {} : { $unset: { lockedAt: 1, lockReason: 1 } }),
        },
      );

      return constructValue<LMSCourseEnrollment>(await enrollCol.findOne<LMSCourseEnrollment>({ _id: enrollmentId }));
    },
});
